import React, { useState } from 'react';
import { Button, message, Tooltip } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

/**
 * "Export PDF" for a report page.
 *
 * Drop it in the page header, point `reportRef` at the element wrapping the
 * report body, and every stat tile and chart inside it lands in the PDF. Tables
 * are passed explicitly so the export carries all their rows rather than only
 * the page the user happens to be on:
 *
 *   tables={[{
 *     title: 'Category Summary',
 *     columns: [
 *       { header: 'Category', key: 'category_name' },
 *       { header: 'Total', key: 'total', align: 'right', format: 'currency' },
 *     ],
 *     rows: data?.by_category,
 *   }]}
 *
 * `format` is one of currency / currency2 / number / decimal / percent / date /
 * text, or a function (value, row) => string.
 *
 * The jsPDF bundle is loaded on demand, so a report page that is never exported
 * never pays for it.
 */
const ExportPdfButton = ({
  reportRef,
  title,
  subtitle,
  meta = [],
  tables = [],
  fileName,
  orientation = 'portrait',
  organizationName,
  disabled = false,
  size = 'small',
  type = 'primary',
  block = false,
  label = 'Export PDF',
}) => {
  const [exporting, setExporting] = useState(false);
  const reduxOrganization = useSelector((state) => state?.auth?.organization);
  const employeeOrganization = useSelector((state) => state?.auth?.user?.employee?.organization);

  const resolvedOrgName = organizationName
    || employeeOrganization?.name
    || (typeof reduxOrganization === 'object' ? reduxOrganization?.name : undefined);

  const slugify = (value) => String(value || 'report')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

  const handleExport = async () => {
    const element = reportRef?.current;
    if (!element) {
      message.warning('There is nothing to export yet.');
      return;
    }

    setExporting(true);
    const hide = message.loading('Preparing PDF...', 0);
    try {
      const { exportReportToPdf } = await import('../../utils/reportPdfExport');
      const stamp = new Date().toISOString().slice(0, 10);
      await exportReportToPdf({
        element,
        title,
        subtitle,
        meta,
        tables,
        orientation,
        organizationName: resolvedOrgName,
        fileName: fileName ? `${fileName}-${stamp}` : `${slugify(title)}-${stamp}`,
      });
      message.success('PDF downloaded');
    } catch (error) {
      console.error('PDF export failed:', error);
      message.error(error?.message || 'Could not export this report as PDF');
    } finally {
      hide();
      setExporting(false);
    }
  };

  return (
    <Tooltip title="Download this report - charts and tables included - as a PDF">
      <Button
        type={type}
        size={size}
        block={block}
        icon={<FilePdfOutlined />}
        loading={exporting}
        disabled={disabled}
        onClick={handleExport}
      >
        {label}
      </Button>
    </Tooltip>
  );
};

export default ExportPdfButton;
